'use client';

import ImageGallery from "../components/imageGallery";
import TrackedLink from "../components/TrackedLink";
import FaqAccordion from "../components/FaqAccordion";
import { track } from "../lib/track";
import { CheckCircle, CalendarCheck, Home, PlaneTakeoff, Utensils, Activity, Wind, Sparkles } from "lucide-react";
import type { Retreat } from "../data/retreats";

const retreatTestimonials = [
  {
    name: "Jayne G.",
    role: "Yoga Retreat Guest, March 2025",
    quote:
      "Niki's passion and enthusiasm is infectious — her patience in answering any question is endless. Everything we learned was accessible and simple to continue at home.",
  },
  {
    name: "Deepa",
    role: "Retreat Guest",
    quote: "Educational, informative and rejuvenating.",
  },
  {
    name: "Ed",
    role: "Retreat Guest",
    quote: "The random conversations I had with like-minded individuals made it.",
  },
  {
    name: "Pam",
    role: "Retreat Guest",
    quote: "Give it a go — open yourself up to different experiences.",
  },
];

const faqs = [
  {
    q: "Who is this retreat for?",
    trackLabel: "Who is this retreat for",
    a: "Anyone ready to step back from the pace of everyday life and invest in their health. You don't need a specific fitness level or prior knowledge — our team meets you exactly where you are. Past guests have ranged from busy professionals and new mums to people recovering from illness or simply feeling stuck.",
  },
  {
    q: "What's the food like?",
    trackLabel: "What is the food like",
    a: "All meals are prepared by Niki and the team using organic, whole-food ingredients aligned with the gut and liver cleanse protocols. Expect nourishing, flavour-forward food — not restriction. There are cooking workshops so you leave knowing how to recreate what you've eaten.",
  },
  {
    q: "Can I come on my own?",
    trackLabel: "Can I come alone",
    a: "Absolutely — most guests do. Retreats naturally bring people together. By day two you'll feel like you've known everyone for years.",
  },
  {
    q: "What level of fitness do I need?",
    trackLabel: "What fitness level is needed",
    a: "None specifically. Movement sessions range from gentle yoga and breathwork to more dynamic options — you choose what feels right each day. Everything is optional.",
  },
  {
    q: "What's included in the price?",
    trackLabel: "What is included in the price",
    a: "Accommodation, all meals, group movement and yoga sessions, breathwork, tai chi, NLP and hypnotherapy workshops, cooking classes, Wim Hof ice bath experience, Kinesio taping, unlimited infrared sauna and detox equipment, a welcome bag worth £120, and 1x lymphatic drainage massage are all included. Additional 1:1 treatments — including IV therapy, Shiatsu, Ayurvedic massage, deep tissue and osteopathy — are available to book on arrival.",
  },
  {
    q: "How many spaces are available?",
    trackLabel: "How many spaces are available",
    a: "We keep numbers intentionally small — usually 14 to 18 guests — so every person gets real attention from the team. Spaces go quickly, especially for La Manga.",
  },
];

const forYouPoints = [
  "You want a genuine reset — gut, liver, nervous system — not just a holiday",
  "You're ready to understand the root causes of how you're feeling",
  "You want expert practitioners in the room, not a one-size-fits-all programme",
  "You leave wanting tools and knowledge you can actually use at home",
  "You're open to new experiences: breathwork, cold exposure, Ayurvedic massage, Shiatsu",
];

interface Props {
  cfRetreats: Retreat[];
  partnerRetreats: Retreat[];
}

export default function RetreatsClient({ cfRetreats, partnerRetreats }: Props) {
  const featured = cfRetreats[0] ?? null;
  const rest = cfRetreats.slice(1);

  const handleVideoPlay = (label: string) => {
    try {
      track(
        "video_play",
        { label, component: "RetreatsPage.Video" },
        { event: "video_play", label, component: "RetreatsPage.Video" }
      );
    } catch {}
  };

  return (
    <main className="min-h-screen flex flex-col">

      {/* ── Hero ── */}
      <section className="relative flex flex-col items-center justify-center text-center px-6 py-24 md:py-36 overflow-hidden">
        <img
          src={featured?.heroImage ?? "/images/laManga.webp"}
          alt={featured?.location ?? "Retreat venue"}
          className="absolute inset-0 w-full h-full object-cover brightness-[0.35]"
        />
        <div className="relative z-10 max-w-3xl mx-auto flex flex-col items-center gap-6">
          {featured ? (
            <>
              <p className="text-xs uppercase tracking-[0.2em] text-white/70 font-medium">
                {featured.location} · {featured.displayDates}
              </p>
              <h1 className="text-3xl md:text-5xl font-black text-white leading-tight">
                Six days that change<br className="hidden md:block" /> how you feel in your body
              </h1>
              <p className="text-base md:text-lg text-white/80 max-w-xl">
                {featured.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <TrackedLink
                  href={featured.bookLink}
                  newTab={featured.isExternalBook}
                  variant="solid"
                  size="lg"
                  color="base"
                  event="click"
                  label="Hero CTA - Reserve Spot"
                  component="RetreatsPage"
                >
                  Reserve Your Spot
                </TrackedLink>
                {featured.brochureLink && (
                  <TrackedLink
                    href={featured.brochureLink}
                    newTab
                    variant="outline"
                    size="lg"
                    color="base"
                    event="click"
                    label="Hero CTA - Download Brochure"
                    component="RetreatsPage"
                  >
                    Download Brochure
                  </TrackedLink>
                )}
              </div>
              {featured.scarcityNote && (
                <p className="text-xs text-white/50">{featured.scarcityNote}</p>
              )}
            </>
          ) : (
            <>
              <h1 className="text-3xl md:text-5xl font-black text-white leading-tight">
                More retreats coming soon
              </h1>
              <p className="text-base text-white/80 max-w-xl">
                We&apos;re planning our next season. Get in touch to be first to hear about new dates.
              </p>
              <TrackedLink
                href="/contactus"
                variant="solid"
                size="lg"
                color="base"
                event="click"
                label="Hero CTA - No Retreats - Contact"
                component="RetreatsPage"
              >
                Get in Touch
              </TrackedLink>
            </>
          )}
        </div>
      </section>

      {/* ── Pull quote ── */}
      <section className="bg-[var(--color-brand)] py-12 px-6 text-center" aria-label="Guest testimonial">
        <blockquote className="max-w-2xl mx-auto">
          <p className="text-white text-xl md:text-2xl font-semibold leading-snug italic">
            &ldquo;Not only did she provide amazingly presented and nutritious meals — she educated
            us and challenged our thinking about food. Everything we learned was accessible and
            simple to continue at home.&rdquo;
          </p>
          <footer className="mt-4 text-white/70 text-sm font-medium tracking-wide uppercase">
            — Jayne G., Yoga Retreat Guest, March 2025
          </footer>
        </blockquote>
      </section>

      {/* ── What you receive ── */}
      <section className="py-16 px-6 bg-[var(--bg-surface-dk)]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black text-[var(--color-text-brand)] text-center mb-2">
            What&apos;s included
          </h2>
          <p className="text-center text-[var(--text-on-light-muted)] text-sm mb-10">
            Everything you need — before you leave, while you&apos;re there, and when you land home.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: <CalendarCheck className="h-8 w-8 text-[var(--color-text-brand)]" />,
                title: "Pre-retreat consultation",
                body: "A 1-hour 1:1 nutrition session with Niki before you travel — so the team understands your health goals from day one.",
              },
              {
                icon: <Home className="h-8 w-8 text-[var(--color-text-brand)]" />,
                title: "6 nights in Villa Gawy",
                body: "A 5-star beachside villa in La Manga — all rooms en suite, two private pools, balconies and free bikes.",
              },
              {
                icon: <PlaneTakeoff className="h-8 w-8 text-[var(--color-text-brand)]" />,
                title: "Airport transfers",
                body: "Included if you travel on the suggested flights — so you arrive as a group and the week starts together.",
              },
            ].map(({ icon, title, body }) => (
              <div key={title} className="bg-[var(--bg-surface)] rounded-2xl p-8 flex flex-col gap-4 items-center text-center">
                {icon}
                <h3 className="font-black text-[var(--color-text-brand)] text-lg leading-snug">{title}</h3>
                <p className="text-sm text-[var(--text-on-light-muted)] leading-relaxed">{body}</p>
              </div>
            ))}
          </div>

          {/* Full included checklist */}
          <div className="mt-8 bg-[var(--bg-surface)] rounded-2xl p-8">
            <h3 className="font-black text-[var(--color-text-brand)] text-lg mb-1">Also included in your package</h3>
            <p className="text-sm text-[var(--text-on-light-muted)] mb-6">All of the below is covered in your retreat price — no surprises on arrival.</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {[
                "1x lymphatic drainage massage",
                "Welcome bag of non-toxic toiletries and supplements worth £120",
                "Nutritionally balanced meals with local organic produce, designed by a Nutritionist",
                "3-day heavy metal liver cleanse",
                "Access to highly absorbable practitioner-strength supplements, herbs and nootropics all week",
                "All functional nutrition and integrative medicine lectures led by specialist practitioners and doctors",
                "Daily functional movement and breathwork classes",
                "Yoga classes",
                "Tai chi classes",
                "Wim Hof breathwork and ice bath experience",
                "NLP and inner confidence workshops",
                "Group hypnotherapy",
                "Hydrotherapy rehabilitation classes",
                "Cooking workshops",
                "Kinesio taping for those injured or in rehabilitation",
                "Unlimited infrared sauna, infrared mat and detoxifying foot ionisers",
                "Sea swimming and beach walks",
                "Access to padel tennis courts",
                "Relaxation time — two beaches and two private pools within the villa grounds",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-[var(--text-on-light-muted)]">
                  <CheckCircle className="h-4 w-4 text-[var(--color-text-brand)] flex-shrink-0 mt-0.5" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Additional payable treatments */}
          <div className="mt-6 rounded-2xl border border-[var(--border-on-light)] p-8">
            <h3 className="font-black text-[var(--color-text-brand)] text-lg mb-1">Additional payable treatments</h3>
            <p className="text-sm text-[var(--text-on-light-muted)] mb-6">A full menu of 1:1 specialist treatments is available to add on throughout the week.</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {[
                "IV Vitamin C and glutathione drip",
                "Vitamin D and B12 injections (if required from test results)",
                "Shiatsu massage",
                "Ayurvedic yoga massage",
                "Additional lymphatic drainage massage",
                "G5 massage or Body Balancer massage",
                "Sacrocranial osteopathy",
                "Deep tissue sport massage",
                "Full body relaxation massage",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-[var(--text-on-light-muted)]">
                  <span className="text-[var(--color-text-brand)] font-bold flex-shrink-0 mt-0.5 w-4 text-center leading-none">+</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── Four pillars ── */}
      <section className="py-16 px-6 bg-[var(--color-bg-muted)]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black text-[var(--color-text-brand)] text-center mb-3">
            Four pillars. One transformative week.
          </h2>
          <p className="text-center text-[var(--text-on-light-muted)] text-sm mb-12 max-w-xl mx-auto">
            The retreat is built around four interwoven disciplines — each addressing a different dimension of your health.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                icon: <Utensils className="h-6 w-6" />,
                label: "The Art of",
                title: "Eating",
                intro: "Led by Niki Charnock & Dr Zoë Lloyd-Wright — every bite is designed with intention.",
                points: [
                  "Organic food and juices crafted by naturopathic nutritionists",
                  "3-day heavy metal liver cleanse built into the week",
                  "Food workshops: chocolate making, raw cakes, fermenting & pickling",
                  "Onsite health bar with practitioner-strength supplements",
                  "Talks on gut-brain connection, minerals, functional testing and anti-cancer nutrition",
                ],
              },
              {
                icon: <Activity className="h-6 w-6" />,
                label: "The Art of",
                title: "Moving",
                intro: "Daily functional movement sessions — all levels welcome, everything optional.",
                points: [
                  "Animal Flow, Tai Chi, yoga and Ashtanga",
                  "KO8 suspension training and water resistance workouts",
                  "Paddle boarding and paddle tennis",
                  "Posture and movement assessments with Niki",
                  "Self-myofascial release and assisted stretching workshops",
                ],
              },
              {
                icon: <Wind className="h-6 w-6" />,
                label: "The Art of",
                title: "Breathing & Being",
                intro: "Practices to reset your nervous system and reconnect with yourself.",
                points: [
                  "Sunrise beach walks, open water swims and breathwork",
                  "Wim Hof breathwork and cold-water immersion with a certified instructor",
                  "Heart Coherence workshop to improve HRV",
                  "Vision boarding, journaling and values workshops",
                  "Restorative yoga, meditation and gentle movement",
                  "Drumming beach ceremony to close the week",
                ],
              },
              {
                icon: <Sparkles className="h-6 w-6" />,
                label: "Bodywork",
                title: "Treatments",
                intro: "Practitioners with 20+ years of experience. Every session tailored to you.",
                points: [
                  "Craniosacral therapy, Shiatsu and stomach massage",
                  "Ayurvedic massage, deep tissue and myofascial release",
                  "Unlimited infrared mat, sauna and ionising foot detox bath",
                  "Unlimited kinesio taping for posture and recovery",
                  "Relaxation massages — for when you just need to deeply restore",
                ],
              },
            ].map((pillar) => (
              <div key={pillar.title} className="bg-[var(--bg-surface)] rounded-2xl p-8 flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  <span className="text-[var(--color-text-brand)]">{pillar.icon}</span>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-[var(--text-on-light-muted)] font-medium">{pillar.label}</p>
                    <h3 className="text-xl font-black text-[var(--color-text-brand)] leading-tight">{pillar.title}</h3>
                  </div>
                </div>
                <p className="text-sm text-[var(--text-on-light-muted)] leading-relaxed italic">{pillar.intro}</p>
                <ul className="flex flex-col gap-2">
                  {pillar.points.map((point) => (
                    <li key={point} className="flex items-start gap-2 text-sm text-[var(--text-on-light-muted)]">
                      <CheckCircle className="h-4 w-4 text-[var(--color-text-brand)] flex-shrink-0 mt-0.5" aria-hidden="true" />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Is this for you ── */}
      <section className="py-16 w-full bg-[var(--bg-surface)]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <h2 className="text-2xl md:text-3xl font-black text-[var(--color-text-brand)] mb-2 text-center">
            Is this retreat for you?
          </h2>
          <p className="text-center text-[var(--text-on-light-muted)] mb-10 text-sm">
            This isn&apos;t a spa break. It&apos;s a structured week of real work on your health — with genuine results.
          </p>
          <ul className="flex flex-col gap-4">
            {forYouPoints.map((point) => (
              <li key={point} className="flex items-start gap-3 bg-[var(--bg-surface-dk)] rounded-xl px-5 py-4">
                <CheckCircle className="h-5 w-5 text-[var(--color-text-brand)] flex-shrink-0 mt-0.5" aria-hidden="true" />
                <span className="text-[var(--text-on-light-muted)]">{point}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── Sample day ── */}
      <section className="py-16 px-6 bg-[var(--bg-surface)]">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black text-[var(--color-text-brand)] text-center mb-3">
            A day at La Manga
          </h2>
          <p className="text-center text-[var(--text-on-light-muted)] text-sm mb-12 max-w-lg mx-auto">
            No two days are identical — but here&apos;s the shape of a typical day to give you a feel for the week.
          </p>
          <div className="flex flex-col">
            {[
              { time: "Sunrise", label: "Beach walk, open water swim & breathwork", detail: "Start the day in the elements — grounding, energising, and genuinely beautiful." },
              { time: "Morning", label: "Movement session", detail: "Animal Flow, yoga, KO8 suspension or Tai Chi — choose what feels right for your body." },
              { time: "Mid-morning", label: "Nutrition workshop or talk", detail: "Niki or Dr Zoë breaks down the science in a way that's immediately useful and easy to take home." },
              { time: "Afternoon", label: "Treatments & free time", detail: "1:1 bodywork sessions, time by the pool, or restoring in the sauna or infrared room." },
              { time: "Evening", label: "Nourishing dinner & community", detail: "Organic food made with intention — and conversations that keep going long after dessert." },
              { time: "Final evening", label: "Drumming beach ceremony", detail: "The week closes on the beach — rhythm, connection, and something you won't forget." },
            ].map((step, i, arr) => (
              <div key={step.time} className="flex gap-6">
                <div className="flex flex-col items-center">
                  <div className="h-10 w-10 rounded-full bg-[var(--color-brand)] flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-bold">{i + 1}</span>
                  </div>
                  {i < arr.length - 1 && <div className="flex-1 w-px bg-[var(--color-brand)] opacity-20 mt-1 mb-1 min-h-[2rem]" />}
                </div>
                <div className="pb-8">
                  <p className="text-xs uppercase tracking-widest text-[var(--color-text-brand)] font-semibold mb-0.5">{step.time}</p>
                  <p className="font-bold text-[var(--color-text-brand)] text-base leading-snug mb-1">{step.label}</p>
                  <p className="text-sm text-[var(--text-on-light-muted)] leading-relaxed">{step.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Featured retreat card ── */}
      {featured && (
        <section aria-labelledby="featured-retreat-heading" className="w-full bg-[var(--color-bg-muted)]">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row overflow-hidden md:rounded-2xl md:my-10">
            <div className="md:w-1/2">
              <img
                src={featured.heroImage}
                alt={featured.name}
                className="w-full h-64 md:h-full object-cover"
              />
            </div>
            <div className="md:w-1/2 p-8 md:p-12 flex flex-col justify-center gap-4">
              <p className="text-xs uppercase tracking-widest text-[var(--color-text-brand)] font-semibold">
                {featured.location} · {featured.displayDates}
              </p>
              <h2 id="featured-retreat-heading" className="text-2xl md:text-3xl font-black text-[var(--color-text-brand)]">
                {featured.name}
              </h2>
              <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">
                {featured.description}
              </p>
              <p className="text-xs text-[var(--color-text-secondary)] italic">
                This is for you if: {featured.forYouIf}
              </p>
              <div className="flex flex-wrap gap-3 mt-2">
                <TrackedLink
                  href={featured.bookLink}
                  newTab={featured.isExternalBook}
                  variant="solid"
                  size="md"
                  color="base"
                  event="click"
                  label={`${featured.id} - Reserve Spot`}
                  component="RetreatsPage"
                >
                  Reserve Your Spot
                </TrackedLink>
                {featured.brochureLink && (
                  <TrackedLink
                    href={featured.brochureLink}
                    newTab
                    variant="outline"
                    size="md"
                    color="neutral"
                    event="click"
                    label={`${featured.id} - Brochure`}
                    component="RetreatsPage"
                  >
                    Full Brochure
                  </TrackedLink>
                )}
                {featured.teamLink && (
                  <TrackedLink
                    href={featured.teamLink}
                    variant="outline"
                    size="md"
                    color="neutral"
                    event="click"
                    label={`${featured.id} - Meet the Team`}
                    component="RetreatsPage"
                  >
                    Meet the Team
                  </TrackedLink>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── Pricing ── */}
      {featured && (
        <section className="py-16 px-6 bg-[var(--bg-surface-dk)]">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-black text-[var(--color-text-brand)] text-center mb-2">
              Pricing &amp; availability
            </h2>
            <p className="text-center text-[var(--text-on-light-muted)] text-sm mb-10">
              {featured.location} · {featured.displayDates} · Limited to 18 guests
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {[ 
               {
                  type: "Single Own Room",
                  price: "£2,300",
                  unit: "per person",
                  desc: "Your own double room — private space and your own en suite.",
                  tag: "Most popular",
                },
                {
                  type: "Couples / Double",
                  price: "£3,000",
                  unit: "per room",
                  desc: "Shared double room for two participants travelling together.",
                  tag: null,
                },
              ].map((option) => (
                <div key={option.type} className="bg-[var(--bg-surface)] rounded-2xl p-8 flex flex-col gap-3 relative">
                  {option.tag && (
                    <span className="absolute top-4 right-4 text-xs bg-[var(--color-brand)] text-white px-2.5 py-0.5 rounded-full font-semibold">
                      {option.tag}
                    </span>
                  )}
                  <h3 className="font-black text-[var(--color-text-brand)] text-lg leading-snug pr-20">{option.type}</h3>
                  <div>
                    <span className="text-3xl font-black text-[var(--color-text-brand)]">{option.price}</span>
                    <span className="text-sm text-[var(--text-on-light-muted)] ml-1">{option.unit}</span>
                  </div>
                  <p className="text-sm text-[var(--text-on-light-muted)] leading-relaxed flex-1">{option.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-center text-sm text-[var(--text-on-light-muted)] mb-8">
              £500 per person deposit secures your place. Balance due one month before the retreat.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <TrackedLink
                href={featured.bookLink}
                newTab={featured.isExternalBook}
                variant="solid"
                size="lg"
                color="base"
                event="click"
                label="Pricing CTA - Reserve with Deposit"
                component="RetreatsPage"
              >
                Reserve with £500 Deposit
              </TrackedLink>
              <TrackedLink
                href="/workwithme/discoverycall"
                variant="outline"
                size="lg"
                color="neutral"
                event="click"
                label="Pricing CTA - Retreat Discovery Call"
                component="RetreatsPage"
              >
                Book a Free Retreat Discovery Call
              </TrackedLink>
            </div>
            <p className="text-center text-xs text-[var(--text-on-light-muted)] mt-4">
              Not sure if this retreat is right for you? A free call with Niki costs nothing and answers everything.
            </p>
          </div>
        </section>
      )}

      {/* ── The villa ── */}
      {featured && (
        <section className="py-16 px-6 bg-[var(--bg-surface)]">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row gap-10 items-center">
            <div className="md:w-1/2 flex flex-col gap-4">
              <p className="text-xs uppercase tracking-widest text-[var(--color-text-brand)] font-semibold">
                La Manga del Mar Menor, Spain
              </p>
              <h2 className="text-2xl md:text-3xl font-black text-[var(--color-text-brand)] leading-tight">
                Villa Gawy
              </h2>
              <p className="text-[var(--text-on-light-muted)] text-sm leading-relaxed">
                A 5-star Scandinavian-style villa set directly on the beachside of La Manga del Mar Menor. Designed for both restoration and community — it&apos;s where the week truly comes alive.
              </p>
              <ul className="flex flex-col gap-2 mt-2">
                {[
                  "Private outdoor pool and garden",
                  "All bedrooms en suite",
                  "Private balconies on top floor rooms",
                  "Air conditioning throughout",
                  "Steps from the beach",
                ].map((point) => (
                  <li key={point} className="flex items-center gap-2 text-sm text-[var(--text-on-light-muted)]">
                    <CheckCircle className="h-4 w-4 text-[var(--color-text-brand)] flex-shrink-0" aria-hidden="true" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
            <div className="md:w-1/2 grid grid-cols-3 gap-3">
              <img
                src="/images/laManga.webp"
                alt="Villa Gawy pool and exterior"
                className="rounded-2xl w-full h-52 object-cover col-span-3"
              />
              <img
                src="/images/retreats.webp"
                alt="Villa Gawy"
                className="rounded-xl w-full h-32 object-cover"
              />
              <img
                src="/images/retreats/6d74a9aa-f90a-4ab6-916c-0003f7a400ab.webp"
                alt="Villa Gawy"
                className="rounded-xl w-full h-32 object-cover"
              />
              <img
                src="/images/retreats/58a39c2b-7d7d-4416-a9c8-0abc1198b389.webp"
                alt="Villa Gawy"
                className="rounded-xl w-full h-32 object-cover"
              />
            </div>
          </div>
        </section>
      )}

      {/* ── More CF retreats (if more than one) ── */}
      {rest.length > 0 && (
        <section aria-label="More CF Organic Wellness retreats" className="w-full py-10 px-6">
          <div className={`max-w-6xl mx-auto grid gap-8 ${rest.length === 1 ? "md:grid-cols-1 max-w-2xl" : "md:grid-cols-2"}`}>
            {rest.map((retreat) => (
              <article key={retreat.id} className="rounded-2xl overflow-hidden bg-[var(--color-bg-muted)] flex flex-col">
                <img src={retreat.heroImage} alt={retreat.name} className="w-full h-52 object-cover" />
                <div className="p-7 flex flex-col gap-3 flex-1">
                  <p className="text-xs uppercase tracking-widest text-[var(--color-text-brand)] font-semibold">
                    {retreat.location} · {retreat.displayDates}
                  </p>
                  <h3 className="text-xl font-black text-[var(--color-text-brand)]">{retreat.name}</h3>
                  <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed flex-1">{retreat.description}</p>
                  <p className="text-xs italic text-[var(--color-text-secondary)]">For you if: {retreat.forYouIf}</p>
                  <div className="flex flex-wrap gap-3">
                    <TrackedLink href={retreat.bookLink} newTab={retreat.isExternalBook} variant="outline" size="md" color="neutral" event="click" label={`${retreat.id} - Reserve`} component="RetreatsPage">
                      Reserve Your Spot
                    </TrackedLink>
                    {retreat.brochureLink && (
                      <TrackedLink href={retreat.brochureLink} newTab variant="outline" size="md" color="neutral" event="click" label={`${retreat.id} - Brochure`} component="RetreatsPage">
                        Brochure
                      </TrackedLink>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* ── Partner retreats ── */}
      {partnerRetreats.length > 0 && (
        <section aria-labelledby="partner-retreats-heading" className="w-full py-12 px-6 bg-[var(--bg-surface)]">
          <div className="max-w-6xl mx-auto">
            <h2 id="partner-retreats-heading" className="text-xl md:text-2xl font-black text-[var(--color-text-brand)] mb-1">
              Also with Niki
            </h2>
            <p className="text-sm text-[var(--text-on-light-muted)] mb-8">
              Niki collaborates with other wellness organisations on these retreats. Booking is handled directly by the host.
            </p>
            <div className={`grid gap-6 ${partnerRetreats.length === 1 ? "max-w-md" : "md:grid-cols-2"}`}>
              {partnerRetreats.map((retreat) => (
                <article key={retreat.id} className="rounded-xl overflow-hidden border border-[var(--border-on-light)] bg-[var(--bg-card)] flex flex-col">
                  {/* Image with logo overlay */}
                  <div className="relative">
                    <img
                      src={retreat.heroImage}
                      alt={retreat.name}
                      className="w-full h-44 object-cover"
                    />
                    {retreat.status === 'nearly-sold-out' && (
                      <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-amber-500/90 text-white text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wide shadow">
                        <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse flex-shrink-0" aria-hidden="true" />
                        1 space left
                      </div>
                    )}
                    {retreat.status === 'booking-soon' && (
                      <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-[var(--color-brand)]/90 text-white text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wide shadow">
                        Booking soon
                      </div>
                    )}
                    {retreat.partnerLogo && (
                      <div className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm rounded-lg px-2 py-1.5 shadow-sm">
                        <img
                          src={retreat.partnerLogo}
                          alt={retreat.partnerName ?? "Partner"}
                          className="h-6 w-auto object-contain"
                        />
                      </div>
                    )}
                  </div>
                  <div className="p-6 flex flex-col gap-3 flex-1">
                    <p className="text-xs uppercase tracking-widest text-[var(--color-text-brand)] font-semibold">
                      {retreat.location} · {retreat.displayDates}
                    </p>
                    <h3 className="text-lg font-bold text-[var(--color-text-brand)]">{retreat.name}</h3>
                    <p className="text-sm text-[var(--text-on-light-muted)] leading-relaxed flex-1">{retreat.description}</p>
                    <div className="flex flex-wrap gap-3 pt-1">
                      <TrackedLink href={retreat.bookLink} newTab={retreat.isExternalBook} variant="outline" size="md" color="neutral" event="click" label={`${retreat.id} - ${retreat.status === 'booking-soon' ? 'Register Interest' : 'Find Out More'}`} component="RetreatsPage">
                        {retreat.status === 'booking-soon' ? 'Register Interest' : 'Find Out More'}
                      </TrackedLink>
                      {retreat.brochureLink && (
                        <TrackedLink href={retreat.brochureLink} newTab variant="outline" size="md" color="neutral" event="click" label={`${retreat.id} - Brochure`} component="RetreatsPage">
                          Brochure
                        </TrackedLink>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Video testimonials ── */}
      <section aria-labelledby="guest-voices" className="bg-[var(--color-brand)] py-16 px-6">
        <h2 id="guest-voices" className="text-2xl md:text-3xl font-black text-white text-center mb-2">
          Hear from past guests
        </h2>
        <p className="text-center text-white/70 text-sm mb-10">Real people. Real results.</p>
        <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {[
            { src: "/images/Deepa_Testimonial_480.mov", label: "Deepa — Retreat Guest" },
            { src: "/images/Pam_Testimonial_480.mov",  label: "Pam — Retreat Guest"   },
            { src: "/images/Ed_Testimonial_480.mov",   label: "Ed — Retreat Guest"    },
          ].map((vid) => (
            <figure key={vid.label} className="bg-white rounded-2xl overflow-hidden shadow-lg">
              <video
                src={vid.src}
                controls
                className="w-full"
                onPlay={() => handleVideoPlay(vid.label)}
              />
              <figcaption className="text-xs text-center py-3 px-4 text-gray-500 font-medium">
                {vid.label}
              </figcaption>
            </figure>
          ))}
        </div>
        <div className="max-w-5xl mx-auto mt-10 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {retreatTestimonials.map((t) => (
            <blockquote
              key={t.name}
              className="bg-white/10 rounded-xl p-5 text-white text-sm italic flex flex-col gap-3"
            >
              <p>&ldquo;{t.quote}&rdquo;</p>
              <footer className="text-white/60 text-xs not-italic">— {t.name}, {t.role}</footer>
            </blockquote>
          ))}
        </div>
      </section>

      {/* ── Team teaser ── */}
      <section aria-labelledby="team-teaser" className="py-16 px-6 text-center bg-[var(--bg-surface)]">
        <h2 id="team-teaser" className="text-2xl md:text-3xl font-black text-[var(--color-text-brand)] mb-3">
          8 expert practitioners. One week.
        </h2>
        <p className="text-[var(--text-on-light-muted)] text-sm max-w-xl mx-auto mb-8">
          Naturopathic nutrition, Ayurvedic massage, integrative medicine, yoga, Shiatsu, Wim Hof
          breathwork, IV therapy, hypnotherapy, and mindset coaching — all under one roof.
        </p>
        <div className="flex flex-wrap justify-center gap-6 mb-10">
          {[
            { name: "Nicola Charnock",  role: "Naturopathic Nutritionist",            img: "/images/NikiTriangle.webp"      },
            { name: "Dr Debs Eastwood", role: "Integrative Medicine & Hypnotherapy",  img: "/images/DrDebsTriange.webp"     },
            { name: "Gill Burt",        role: "Wim Hof & HeartMath Coach",            img: "/images/GillBurtTriangle.webp"  },
            { name: "John Wilson",      role: "Shiatsu & Shamanic Healing",           img: "/images/JohnShiatsuTriangle.webp"},
          ].map((p) => (
            <div key={p.name} className="flex flex-col items-center gap-2 w-40">
              <img
                src={p.img}
                alt={p.name}
                className="w-24 h-24 rounded-full object-cover object-top shadow-md"
              />
              <p className="text-sm font-bold text-[var(--color-text-brand)] text-center leading-snug">{p.name}</p>
              <p className="text-xs text-[var(--text-on-light-muted)] text-center leading-snug">{p.role}</p>
            </div>
          ))}
        </div>
        <TrackedLink
          href="/retreats/team"
          variant="outline"
          size="md"
          color="neutral"
          event="click"
          label="Team Teaser - Meet Full Team"
          component="RetreatsPage"
        >
          Meet the Full Team
        </TrackedLink>
      </section>

      {/* ── FAQ ── */}
      <section id="faqs" aria-labelledby="faq-heading" className="py-16 px-6 bg-[var(--color-bg-muted)]">
        <div className="max-w-2xl mx-auto">
          <h2 id="faq-heading" className="text-2xl md:text-3xl font-black text-[var(--color-text-brand)] text-center mb-10">
            Common questions
          </h2>
          <FaqAccordion faqs={faqs} />
        </div>
      </section>

      {/* ── Gallery ── */}
      <ImageGallery />

      {/* ── Final CTA ── */}
      <section className="py-20 px-6 text-center bg-[var(--color-brand)]">
        <h2 className="text-2xl md:text-3xl font-black text-white mb-3">
          Ready to reset?
        </h2>
        {featured ? (
          <p className="text-white/70 text-sm mb-8 max-w-md mx-auto">
            {featured.location} · {featured.displayDates} — {featured.scarcityNote ?? "spaces are limited"}. If you&apos;re sitting on the fence, reach out first.
          </p>
        ) : (
          <p className="text-white/70 text-sm mb-8 max-w-md mx-auto">
            New dates coming soon. Get in touch to be first to hear about them.
          </p>
        )}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {featured && (
            <TrackedLink
              href={featured.bookLink}
              newTab={featured.isExternalBook}
              variant="solid"
              size="lg"
              color="oat"
              event="click"
              label="Final CTA - Reserve Spot"
              component="RetreatsPage"
            >
              Reserve Your Spot
            </TrackedLink>
          )}
          <TrackedLink
            href="/contactus"
            variant="outline"
            size="lg"
            color="oat"
            event="click"
            label="Final CTA - Contact Us"
            component="RetreatsPage"
          >
            {featured ? "Ask a Question" : "Get in Touch"}
          </TrackedLink>
        </div>
      </section>

    </main>
  );
}
